import React, { useMemo, useState } from 'react';
import {
  Box,
  Heading,
  Button,
  Tabs,
  TabList,
  TabItem,
  TabPanel,
  ListView,
  useToast,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import { usePagination } from 'hooks/usePagination';
import { ClaimsFilters } from './ClaimsFilters';
import { ClaimsListView } from './ClaimsListView';
import { ClaimsDetailView } from './ClaimsDetailView';
import { YTDSummaryTable } from './YTDSummaryTable';
import { ClaimFilterType, ClaimCounts, ListViewTableHeaderData } from './types';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { useFilters } from 'hooks/useFilters';
import { claimFiltersMap } from './constants';
import { STATUS_DISPLAY_MAP, getNextWindowStartDateLabel } from './utils';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { getFlexiReimbursementClaimsList } from 'api/gql-utils/flexibleBenefits/queries/getFlexiReiumbursementClaimsList';
import { getFlexibleBenefitReport } from 'api/gql-utils/flexibleBenefits/queries/getFlexibleBenefitReport';
import useReduxSelector from 'utils/useReduxSelector';
import { useSelector } from 'react-redux';
import api from 'api';
import { ComponentModule } from 'api/gql-utils/bonusAssignment/types';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { generateUserId } from 'utils/commons';
import { useSearchParams } from 'hooks/useSearchParams';
import { BladeTooltipWrapper } from 'components/ui/BladeTooltipWrapper/BladeTooltipWrapper';
import { FlexiReimbursementClaim } from 'api/gql-utils/flexibleBenefits/types';

export const ClaimsView = () => {
  const { urlParams } = useSearchParams();
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const [totalClaimsCount, setTotalClaimsCount] = useState(0);

  const { openDetailsView } = useDetailsView('claimId');
  const { currentFilters } = useFilters({
    filtersMap: claimFiltersMap,
  });

  const activeUserId = useSelector(loggedInUserSelectors.peopleId)?.toString() || '';

  const user_id = urlParams.get('userId') || activeUserId;

  const { data, isLoading, isError, refetch, isRefetching, dataUpdatedAt } = useQuery({
    queryKey: [
      getFlexiReimbursementClaimsList,
      currentFilters.benefitType,
      currentPage,
      itemsPerPage,
    ],
    queryFn: () =>
      getFlexiReimbursementClaimsList({
        offset: currentPage * itemsPerPage,
        limit: itemsPerPage,
        ...(currentFilters?.benefitType?.length > 0
          ? { benefit_type: currentFilters.benefitType?.split(',') || [] }
          : {}),
        ...(user_id ? { user_id } : {}),
      }),
    select: (data) => data.payrollListFlexiReimbursementClaims,
    onSuccess: (data) => {
      setTotalClaimsCount(data.total_count);
    },
  });

  let organizationId = `org_${useReduxSelector(
    (state) => state?.loggedInUser?.data?.currentOrganization?.id || '',
  )}`;

  const toast = useToast();

  const { data: reportData, isLoading: isReportsLoading } = useQuery({
    queryKey: [getFlexibleBenefitReport, organizationId],
    queryFn: () =>
      getFlexibleBenefitReport({
        merchant_id: organizationId,
        tenant_id: organizationId,
        filters: {
          user_ids: [generateUserId(user_id)],
        },
      }),
    select: (data) => data?.payrollGetFBPReport?.employee_reports?.[0]?.components || [],
    onError: (error) => {
      toast.show({
        content: 'Failed to load FBP Reports data, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const { data: flexibleBenefitsListData, isLoading: isLoadingFlexibleBenefitsList } = useQuery({
    queryKey: ['flexibleComponentListData'],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefitsList({
        offset: 0,
        limit: 100,
        type: ComponentModule.FBP,
      }),
    select: (data) => data.payrollListFlexibleBenefitsComponents.data,
    onError: (error) => {
      toast.show({
        content: 'Failed to load flexible components list, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const fbpReportsData = useMemo(() => {
    return reportData?.filter((report) =>
      flexibleBenefitsListData
        ?.filter((benefit) => benefit.name === report.component_name)
        .map((benefit) => {
          return { ...benefit, component_name: benefit.display_name };
        }),
    );
  }, [reportData, flexibleBenefitsListData]);

  const employee_id = useSelector(loggedInUserSelectors.peopleId)?.toString() || '';

  const { data: proofUploadWindowData, isLoading: isProofUploadWindowLoading } = useQuery({
    queryKey: ['getProofUploadWindowData', employee_id],
    queryFn: () => {
      return api.flexibleBenefitComponents.queries.getEffectiveSetting({
        setting_name: 'proof_upload_window_config',
        employee_id,
      });
    },
    enabled: !!employee_id,
    select: (data) => data?.payrollGetEffectiveSetting?.data?.map_value,
  });

  const isProofUploadWindowOpen = useMemo(() => {
    if (!proofUploadWindowData) return false;
    const currentTimestamp = Date.now();
    const currentDayOfMonth = new Date().getDate();

    switch (proofUploadWindowData.active_key) {
      case 'every_month_at_period':
        return (
          proofUploadWindowData?.values?.every_month_at_period?.number_range_value?.value?.start <=
            currentDayOfMonth &&
          proofUploadWindowData?.values?.every_month_at_period?.number_range_value?.value?.end >=
            currentDayOfMonth
        );
      case 'custom_range':
        return proofUploadWindowData?.values?.custom_range?.date_ranges_value?.values.some(
          (value: { start: number; end: number }) => {
            return value.start * 1000 <= currentTimestamp && value.end * 1000 >= currentTimestamp;
          },
        );
      default:
        return true;
    }
  }, [proofUploadWindowData]);

  const selectedFilter = (currentFilters.status as ClaimFilterType) || 'all';

  const filteredData: FlexiReimbursementClaim[] =
    selectedFilter === 'all'
      ? data?.claims || []
      : (data?.claims || []).filter((claim: FlexiReimbursementClaim) => {
          const displayStatus = STATUS_DISPLAY_MAP[claim.claim_status] || claim.claim_status;
          return displayStatus === selectedFilter.toUpperCase();
        });

  const counts: ClaimCounts = {
    all: data?.claims?.length || 0,
    pending: (data?.claims || []).filter(
      (claim: FlexiReimbursementClaim) => claim.claim_status === 'PENDING',
    ).length,
    rejected: (data?.claims || []).filter(
      (claim: FlexiReimbursementClaim) => claim.claim_status === 'REJECTED',
    ).length,
    approved: (data?.claims || []).filter((claim: FlexiReimbursementClaim) => {
      const displayStatus = STATUS_DISPLAY_MAP[claim.claim_status] || claim.claim_status;
      return displayStatus === 'APPROVED';
    }).length,
    submitted: (data?.claims || []).filter(
      (claim: FlexiReimbursementClaim) => claim.claim_status === 'SUBMITTED',
    ).length,
  };

  const headers: ListViewTableHeaderData[] = [
    { name: 'Request date' },
    { name: 'Benefit type' },
    { name: 'Approval Status' },
    { name: 'Request Amount' },
    { name: 'Approved Amount' },
  ];

  const handleRowClick = (claimId: string) => {
    openDetailsView(claimId);
  };

  const isClaimsLoading =
    isLoading || isProofUploadWindowLoading || isReportsLoading || isLoadingFlexibleBenefitsList;

  const history = useHistory();

  const isProofUploadWindowClosed = !isProofUploadWindowLoading && !isProofUploadWindowOpen;

  return (
    <Box paddingX="spacing.8" marginTop="spacing.5" position="relative">
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="spacing.5"
        marginTop="spacing.8">
        <Heading size="large">Flexi Reimbursement proof upload</Heading>
        <BladeTooltipWrapper
          content={
            isProofUploadWindowClosed
              ? `Proof upload window is currently closed. Next window opens on ${
                  getNextWindowStartDateLabel(proofUploadWindowData) || ''
                }`
              : ''
          }>
          <Button
            size="medium"
            isDisabled={isProofUploadWindowClosed}
            onClick={() => {
              history.push({
                pathname: routePaths.flexibleBenefits.claims.create,
                search: `userId=${user_id?.toString() || ''}`,
              });
            }}>
            Create new Claim
          </Button>
        </BladeTooltipWrapper>
      </Box>

      <Tabs>
        <TabList>
          <TabItem value="flexiReimbursement">My Flexi Reimbursement</TabItem>
          <TabItem value="ytdSummary">YTD summary</TabItem>
        </TabList>
        <TabPanel value="flexiReimbursement">
          <ListView>
            {!isProofUploadWindowClosed && <ClaimsFilters counts={counts} />}
            <ClaimsListView
              data={filteredData}
              isLoading={isClaimsLoading}
              isRefreshing={isRefetching}
              isError={isError}
              headers={headers}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              setCurrentPage={setCurrentPage}
              setItemsPerPage={setItemsPerPage}
              onRowClick={handleRowClick}
              selectedFilter={selectedFilter}
              onRefetch={refetch}
              totalCount={totalClaimsCount}
              dataUpdatedAt={dataUpdatedAt}
            />
          </ListView>
        </TabPanel>
        <TabPanel value="ytdSummary">
          <YTDSummaryTable data={fbpReportsData || []} isLoading={isReportsLoading} />
        </TabPanel>
      </Tabs>

      <ClaimsDetailView />
    </Box>
  );
};
